import { Button, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { HttpUpdateForm } from '@/api';
import { showApiErrorNotification } from '@/api/utils/notifications';
import { CodeBlock } from '@/components/Ui/CodeBlock';
import { CopyButton } from '@/components/Ui/CopyButton';
import { InlineTextEditor } from '@/components/Ui/InlineTextEditor';
import { LedgerRow } from '@/components/Ui/LedgerRow';
import { Section } from '@/components/Ui/Section';
import { SegmentedButtons } from '@/components/Ui/SegmentedButtons';
import { SwitchRow } from '@/components/Ui/SwitchRow';
import { useUndoToast } from '@/components/Ui/Toast';
import {
  type AutoReplyText,
  autoReplyTextErrors,
  EXAMPLE_SUBJECTS,
  hasEmailField,
  templateExample,
  templateVariables,
} from '@/lib/auto-reply';
import { fieldErrorMessages, validationErrors } from '@/lib/field-errors';
import { hasFields } from '@/lib/form-site';
import { validateTemplateString } from '@/lib/template-validation';
import { useRustyState } from '@/state';
import classes from './FormOverview.module.css';
import type { SectionProps } from './types';

type Prereq =
  | 'no_captcha'
  | 'no_recipients'
  | 'missing_subject'
  | 'missing_text'
  | 'no_email_field';

function TemplateEditor({
  value,
  multiline,
  label,
  placeholder,
  required,
  error,
  onSave,
  close,
}: {
  value: string;
  multiline?: boolean;
  label: string;
  placeholder?: string;
  /** Message shown when the text is cleared while auto-reply is on; omitted when it may be empty. */
  required?: string;
  /** The server's refusal of the last save. */
  error?: string;
  onSave: (next: string) => Promise<boolean>;
  close: () => void;
}) {
  const { t } = useTranslation();
  const [localError, setLocalError] = useState<string | null>(null);
  return (
    <InlineTextEditor
      value={value}
      multiline={multiline}
      label={label}
      placeholder={placeholder}
      error={localError ?? error}
      help={t('form.autoReply.templateHint', { example: '{{ field | "fallback" }}' })}
      onCancel={close}
      onSave={async (next) => {
        if (required && !next.trim()) {
          setLocalError(required);
          throw new Error('required');
        }
        const check = validateTemplateString(next);
        if (!check.isValid) {
          setLocalError(check.errors.join(' '));
          throw new Error('invalid template');
        }
        setLocalError(null);
        if (!(await onSave(next))) throw new Error('rejected');
        close();
      }}
    />
  );
}

function SendTest({ formId, email }: { formId: string; email: string }) {
  const { t } = useTranslation();
  const notify = useUndoToast();
  const [to, setTo] = useState(email);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const send = async () => {
    setBusy(true);
    setError(null);
    setSetupError(null);
    try {
      await useRustyState
        .getState()
        .api.sendAutoResponseTest(formId, to, { onSuccess: false, onError: false });
      notify(t('form.autoReply.testSent', { email: to }));
    } catch (e) {
      const raw = validationErrors(e);
      if (raw) {
        const { email: emailError, ...rest } = fieldErrorMessages(raw, t);
        setError(emailError ?? null);
        const other = Object.values(rest).join(' ');
        setSetupError(other || null);
      } else if ((e as { response?: unknown })?.response) {
        showApiErrorNotification(e, notifications);
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={classes.stack}>
      <form
        className={classes.inlineForm}
        onSubmit={(e) => {
          e.preventDefault();
          if (to.trim() && !busy) void send();
        }}
      >
        <TextInput
          label={t('form.autoReply.testLabel')}
          value={to}
          onChange={(e) => {
            setTo(e.currentTarget.value);
            setError(null);
          }}
          placeholder="you@example.com"
          className={classes.grow}
          error={error}
        />
        <Button type="submit" loading={busy} disabled={!to.trim()}>
          {t('form.autoReply.testButton')}
        </Button>
      </form>
      {setupError && (
        <span className={classes.risk} role="alert">
          {setupError}
        </span>
      )}
    </div>
  );
}

export function AutoReplySection({
  form,
  save,
  errors,
  setErrors,
  data,
  spec,
}: Omit<SectionProps, 'method' | 'hasMessages'>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const nostr = form.encryption_mode === 'nostr';
  const enabled = form.auto_response_enabled;
  const subject = form.auto_response_subject ?? '';
  const text = form.auto_response_text ?? '';
  const verified = (data.recipients ?? []).filter((r) => r.is_verified);
  const firstRecipient = verified[0]?.email ?? '';
  const fields = hasFields(spec);

  const status: Record<Prereq, boolean | null> = {
    no_captcha: form.check_challenge,
    no_recipients: data.recipients === null ? null : verified.length > 0,
    no_email_field: fields ? hasEmailField(spec) : null,
    missing_subject: subject.trim().length > 0,
    missing_text: text.trim().length > 0,
  };
  const skipProblem = data.problems?.find((p) => p.cause === 'auto_reply_skipped');
  const skipReason =
    (skipProblem?.detail as Prereq | undefined) ??
    (Object.keys(status) as Prereq[]).find((k) => status[k] === false);

  const value = !enabled
    ? t('ui.off')
    : skipReason
      ? t('form.autoReply.onSkipped', {
          reason: t(`form.autoReply.skip.${skipReason}`, { defaultValue: skipReason }),
        })
      : t('ui.on');

  const mark = (ok: boolean | null) =>
    ok === null ? '' : ok ? t('form.autoReply.met') : t('form.autoReply.unmet');
  const needs = t('form.autoReply.needs', {
    captcha: mark(status.no_captcha),
    recipient: mark(status.no_recipients),
    subject: mark(status.missing_subject),
    text: mark(status.missing_text),
    emailField: fields
      ? t('form.autoReply.emailFieldSpec', { mark: mark(status.no_email_field) })
      : t('form.autoReply.emailFieldOwn'),
  });

  const saveText = (patch: HttpUpdateForm, message: string) => save(patch, message);
  const requiredMessage = (field: AutoReplyText) => t(`form.fieldErrors.${field}.required`);

  const toggle = (next: boolean) => {
    const missing = autoReplyTextErrors({ enabled: next, subject, text });
    if (missing.length > 0) {
      setErrors(Object.fromEntries(missing.map((f) => [f, requiredMessage(f)])));
      return;
    }
    void save(
      { auto_response_enabled: next },
      t(next ? 'ui.turnedOn' : 'ui.turnedOff', { name: t('form.autoReply.title') })
    );
  };
  const variables = templateVariables(spec);

  if (nostr) {
    return (
      <Section id="auto-reply" title={t('form.autoReply.title')}>
        <LedgerRow
          label={t('form.autoReply.status')}
          value={t('form.autoReply.unavailable')}
          help={t('form.autoReply.unavailableHelp')}
        />
      </Section>
    );
  }

  return (
    <Section id="auto-reply" title={t('form.autoReply.title')}>
      <LedgerRow
        label={t('form.autoReply.status')}
        value={
          <>
            <strong>{value}</strong>
            {subject && <span>{t('form.autoReply.subjectLine', { subject })}</span>}
          </>
        }
        risk={enabled && skipReason ? t('form.autoReply.skippedRisk') : undefined}
        help={`${t('form.autoReply.what')} ${needs} ${t('form.autoReply.cost')}`}
        action={
          <Button variant="subtle" size="xs" aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? t('ui.close') : enabled || subject ? t('ui.edit') : t('form.autoReply.setUp')}
          </Button>
        }
      />
      {open && (
        <>
          <SwitchRow
            label={t('form.autoReply.enabled')}
            checked={enabled}
            onChange={toggle}
            risk={errors.auto_response_enabled}
            help={t('form.autoReply.enabledHelp')}
          />
          <LedgerRow
            label={t('form.autoReply.subject')}
            value={subject ? subject : t('form.autoReply.empty')}
            risk={errors.auto_response_subject}
            editor={{
              render: (close) => (
                <TemplateEditor
                  value={subject}
                  label={t('form.autoReply.subject')}
                  placeholder={EXAMPLE_SUBJECTS[0]}
                  required={enabled ? requiredMessage('auto_response_subject') : undefined}
                  error={errors.auto_response_subject}
                  close={close}
                  onSave={(next) =>
                    saveText(
                      { auto_response_subject: next.trim() },
                      t('form.autoReply.subjectSaved')
                    )
                  }
                />
              ),
            }}
          />
          <LedgerRow
            label={t('form.autoReply.text')}
            value={
              text ? <span className={classes.preWrap}>{text}</span> : t('form.autoReply.empty')
            }
            risk={errors.auto_response_text}
            editor={{
              render: (close) => (
                <TemplateEditor
                  value={text}
                  multiline
                  label={t('form.autoReply.text')}
                  required={enabled ? requiredMessage('auto_response_text') : undefined}
                  error={errors.auto_response_text}
                  close={close}
                  onSave={(next) =>
                    saveText({ auto_response_text: next }, t('form.autoReply.textSaved'))
                  }
                />
              ),
            }}
          />
          <LedgerRow
            label={t('form.autoReply.format')}
            value={t(`form.after.format.${form.auto_response_format === 'text' ? 'text' : 'html'}`)}
            editor={{
              render: (close) => (
                <SegmentedButtons
                  aria-label={t('form.autoReply.format')}
                  options={[
                    { value: 'html', label: t('form.after.format.html') },
                    { value: 'text', label: t('form.after.format.text') },
                  ]}
                  value={form.auto_response_format === 'text' ? 'text' : 'html'}
                  onChange={(v) => {
                    close();
                    void save(
                      { auto_response_format: v },
                      t('form.autoReply.formatSaved', { format: t(`form.after.format.${v}`) })
                    );
                  }}
                />
              ),
            }}
          />
          <div className={classes.block}>
            <strong>{t('form.autoReply.variablesTitle')}</strong>
            <span className={classes.help}>
              {fields
                ? t('form.autoReply.variablesFromFields')
                : t('form.autoReply.variablesExamples')}
            </span>
            <div className={classes.vars}>
              {variables.map((v) => (
                <span key={v.name}>
                  <code className={classes.code}>{`{{ ${v.name} | "fallback" }}`}</code>{' '}
                  <span className={classes.help}>{v.description}</span>
                </span>
              ))}
            </div>
            <strong>{t('form.autoReply.exampleSubjects')}</strong>
            <div className={classes.vars}>
              {EXAMPLE_SUBJECTS.map((s) => (
                <span key={s} className={classes.row}>
                  <code className={classes.code}>{s}</code>
                  <CopyButton text={s} variant="subtle" />
                </span>
              ))}
            </div>
            <strong>{t('form.autoReply.exampleText')}</strong>
            <CodeBlock code={templateExample(spec)} copy wrap />
            <SendTest formId={form.id} email={firstRecipient} />
          </div>
        </>
      )}
    </Section>
  );
}
